import {
  BookOpenIcon,
  CloudIcon,
  Cog6ToothIcon,
  HomeIcon,
  MagnifyingGlassIcon,
  PresentationChartLineIcon,
  ShieldCheckIcon,
  ShoppingBagIcon,
  NewspaperIcon,
  UserIcon
} from '@heroicons/react/24/outline';
import {
  weatherAgronomyGroups,
  getWeatherAgronomyGroupModules
} from './weatherAgronomyModules';
import { weatherAgronomyModules } from './weatherAgronomyModules';
import { systemManagementModules, matchSystemManagementModule, getVisibleSystemManagementModules } from './systemManagementModules';
import { profileModules } from './profileModules';
import { hasPagePermission, canReviewTenantProvision, canReviewMemberApplication } from '../utils/permissions';

export const SIDEBAR_WIDTH_CLASS = 'md:left-[168px]';

function leaf(title, path, icon) {
  return { title, path, icon, children: [] };
}

function group(title, path, children) {
  return { title, path, children: children || [] };
}

/** 生产管理二级：进枢纽并切到对应分区；三级：与 /production 功能卡片一致的页面跳转 */
function buildProductionChildren(t) {
  return [
    group(t('mpp_dim_land'), '/production?dim=land', [
      leaf(t('page_title_my_farm'), '/production/land'),
      leaf(t('page_title_asset_management') || '资产管理', '/production/land/asset-management')
    ]),
    group(t('mpp_dim_crop'), '/production?dim=crop', [
      leaf(t('page_title_planted_crops'), '/production/crop/plans'),
      leaf('作物全域分布', '/production/crop/global-distribution'),
      leaf('作物长势对比', '/production/crop/growth-comparison')
    ]),
    group(t('mpp_dim_field_work'), '/production?dim=field-work', [
      leaf('农事作业总览', '/production/field-work'),
      leaf(t('page_title_field_management') || '农事管理', '/production/field-work/plans'),
      leaf('病虫害防治', '/production/field-work/pest-control'),
      leaf(t('page_title_warning_management') || '预警管理', '/production/field-work/warning-management')
    ]),
    group(t('mpp_dim_support'), '/production?dim=support', [
      leaf(t('page_title_agricultural_materials') || '农资管理', '/production/support/agricultural-materials'),
      leaf('采购管理', '/production/support/farm-procurement'),
      leaf('粮仓管理', '/production/support/grain-storage'),
      leaf(t('page_title_data_statistics') || '营收统计', '/production/land/business-analysis')
    ])
  ];
}

function buildAgriNewsChildren(t) {
  return [
    group(t('ppn_category_news'), '/agri-news/news-center', [
      leaf(t('ppn_news_center_title') || '资讯中心', '/agri-news/news-center'),
      leaf('区域资讯', '/agri-news/regional-news'),
      leaf('国际资讯', '/agri-news/international-news'),
      leaf('政策解读', '/agri-news/policy-interpretation'),
      leaf('技术指导', '/agri-news/technical-guidance'),
      leaf(t('ppn_research_progress_title') || '科研进展', '/agri-news/research-progress')
    ]),
    group(t('ppn_category_warning'), '/agri-news/pest-warning', [
      leaf(t('ppn_pest_warning_title') || '病虫害预警', '/agri-news/pest-warning'),
      leaf(t('ppn_market_analysis_title') || '市场分析', '/agri-news/market-analysis'),
      leaf(t('ppn_data_statistics_title') || '数据统计', '/agri-news/data-statistics'),
      leaf(t('ppn_network_trend_analysis_title') || '网络趋势分析', '/agri-news/network-trend-analysis')
    ]),
    group(t('ppn_category_environment'), '/agri-news/soil-analysis', [
      leaf(t('ppn_soil_analysis_title') || '土壤分析', '/agri-news/soil-analysis'),
      leaf(t('ppn_water_resources_title') || '水资源', '/agri-news/water-resources')
    ]),
    group(t('ppn_category_guidance'), '/agri-news/planting-advice', [
      leaf(t('ppn_seasonal_guidance_title') || '季节性指导', '/agri-news/seasonal-guidance'),
      leaf(t('ppn_expert_advice_title') || '专家建议', '/agri-news/expert-advice'),
      leaf('农民学院', '/agri-news/farmer-academy'),
      leaf(t('ppn_planting_advice_title') || '种植建议', '/agri-news/planting-advice')
    ]),
    group(t('ppn_category_safety'), '/agri-news/safe-pesticide-use', [
      leaf(t('ppn_safe_pesticide_use_title') || '安全用药', '/agri-news/safe-pesticide-use'),
      leaf(t('ppn_environmental_protection_title') || '环保要求', '/agri-news/environmental-protection')
    ])
  ];
}

function buildWeatherAgronomyChildren() {
  return weatherAgronomyGroups.map((g) => {
    const children = getWeatherAgronomyGroupModules(g).map((m) => leaf(m.title, m.path, m.icon));
    return group(g.title, g.path, children);
  });
}

function buildSystemManagementChildren() {
  return getVisibleSystemManagementModules().map((module) => {
    const base = leaf(module.title, module.path, module.icon);
    if (module.id === 'organization-user') {
      const orgChildren = [
        leaf('组织用户管理', '/system-management/organization-user'),
        leaf('组织机构', '/system-management/organization'),
        leaf('用户管理', '/system-management/user'),
        leaf('角色管理', '/system-management/role'),
        leaf('权限管理', '/system-management/permission'),
        leaf('页面权限', '/system-management/page-permission'),
        leaf('用户角色', '/system-management/user-role'),
        leaf('角色权限', '/system-management/role-permission')
      ];
      if (canReviewMemberApplication()) {
        orgChildren.splice(3, 0, leaf('成员申请', '/system-management/tenant-member-applications'));
      }
      base.children = orgChildren;
    } else if (module.id === 'tenant' && canReviewTenantProvision()) {
      base.children = [
        leaf('租户管理', '/system-management/tenant'),
        leaf('开通审核', '/system-management/tenant?tab=provision')
      ];
    } else if (module.id === 'ai') {
      base.children = [
        leaf('AI 设置', '/system-management/ai-settings'),
        leaf('模型配置', '/system-management/ai-model-config'),
        leaf('提示词配置', '/system-management/ai-prompt-config'),
        leaf('提示词资产', '/system-management/prompt-assets'),
        leaf('Prompt Ops', '/system-management/prompt-ops'),
        leaf('缓存管理', '/system-management/cache-management')
      ];
    } else if (module.id === 'security') {
      base.children = [
        leaf('安全设置', '/system-management/security-settings')
      ];
    }
    return base;
  });
}

function buildMallChildren() {
  return [
    leaf('商城首页', '/mall'),
    leaf('我的商城', '/mall/my-mall'),
    leaf('商城管理', '/mall/management')
  ];
}

function toProfileChildren() {
  return profileModules.map((module) => leaf(module.title, module.path, module.icon));
}

export function createMenuItems(user, t) {
  return [
    {
      title: t('nav_smart_farm_cockpit'),
      path: '/smart-farm-cockpit',
      icon: PresentationChartLineIcon,
      openInNewTab: true,
      children: []
    },
    {
      title: t('nav_welcome'),
      path: '/welcome',
      icon: HomeIcon,
      children: []
    },
    {
      title: t('nav_my_plant_protection'),
      path: '/production',
      icon: ShieldCheckIcon,
      children: buildProductionChildren(t)
    },
    {
      title: t('nav_plant_protection_news'),
      path: '/agri-news',
      icon: NewspaperIcon,
      children: buildAgriNewsChildren(t)
    },
    {
      title: t('nav_weather_agronomy'),
      path: '/weather-agronomy',
      icon: CloudIcon,
      children: buildWeatherAgronomyChildren()
    },
    {
      title: t('nav_plant_protection_inquiry'),
      path: '/ai-service',
      icon: MagnifyingGlassIcon,
      children: []
    },
    {
      title: t('nav_knowledge'),
      path: '/knowledge',
      icon: BookOpenIcon,
      children: []
    },
    {
      title: t('nav_plant_protection_mall'),
      path: '/plant-protection-mall',
      icon: ShoppingBagIcon,
      children: buildMallChildren()
    },
    {
      title: t('nav_profile'),
      path: '/profile',
      icon: UserIcon,
      children: toProfileChildren()
    },
    {
      title: t('nav_system_management'),
      path: '/system-management',
      icon: Cog6ToothIcon,
      children: buildSystemManagementChildren()
    }
  ];
}

export function hasMenuPermission(item, user) {
  if (!user) return false;
  if (item.path === '/profile') return true;
  if (item.path === '/system-management') {
    return getVisibleSystemManagementModules().length > 0;
  }
  if (item.permission) {
    return hasPagePermission(item.permission);
  }
  return true;
}

/**
 * 顶栏/侧栏一级菜单是否选中。
 * 注意：气象农艺配置里复用了部分 /agri-news/* 页（土壤/水情/种植建议），
 * 一级高亮必须以 URL 前缀互斥，避免与「农业资讯」同时选中。
 */
export function isMenuActive(item, pathname) {
  if (!item?.path || !pathname) return false;

  if (item.path === '/system-management') {
    if (pathname === '/system-management' ||
      pathname.startsWith('/system-management/') ||
      pathname.startsWith('/system-settings')) {
      return true;
    }
    return systemManagementModules.some((child) => matchSystemManagementModule(child, pathname));
  }
  if (item.path === '/weather-agronomy') {
    return pathname === '/weather-agronomy' || pathname.startsWith('/weather-agronomy/');
  }
  if (item.path === '/agri-news') {
    return pathname === '/agri-news' || pathname.startsWith('/agri-news/');
  }
  if (item.path === '/profile') {
    return pathname === '/profile' || pathname.startsWith('/profile/');
  }
  if (item.path === '/smart-farm-cockpit') {
    return pathname === '/smart-farm-cockpit' || pathname.startsWith('/smart-farm-cockpit/');
  }
  if (item.path === '/production') {
    return pathname === '/production' ||
      pathname.startsWith('/production/') ||
      pathname.startsWith('/production/crop/models/');
  }
  if (item.path === '/plant-protection-mall') {
    return pathname === '/mall' || pathname.startsWith('/mall/') ||
      pathname === '/plant-protection-mall' || pathname.startsWith('/plant-protection-mall/');
  }
  if (item.path === '/knowledge') {
    return pathname === '/knowledge' || pathname.startsWith('/knowledge/') ||
      pathname === '/knowledge-base' || pathname.startsWith('/knowledge-base/');
  }
  return pathname === item.path || pathname.startsWith(`${item.path}/`);
}
